import React, { useState, useEffect } from 'react';
import { usuariosAPI, rolesAPI, sucursalesAPI, permisosAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { 
  Users, Plus, Edit2, Trash2, X, Eye, EyeOff, 
  Shield, UserCheck, UserX, Mail, Phone, Building2, Lock, CheckSquare, Square
} from 'lucide-react';

const UsuariosPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [permisos, setPermisos] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPermisos, setShowPermisos] = useState(false);
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre_completo: '',
    email: '',
    telefono: '',
    rol_id: '',
    sucursal_id: '',
    comision_porcentaje: 0,
    activo: 1,
    permisos: []
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [usuariosRes, rolesRes, sucursalesRes, permisosRes] = await Promise.all([
        usuariosAPI.getAll(),
        rolesAPI.getAll(),
        sucursalesAPI.getAll().catch(() => ({ data: { success: false } })),
        permisosAPI.getModulos().catch(() => ({ data: { success: false } }))
      ]);
      
      if (usuariosRes.data.success) {
        setUsuarios(usuariosRes.data.data);
      }
      
      if (rolesRes.data.success) {
        setRoles(rolesRes.data.data.filter(r => r.activo));
      }
      
      if (sucursalesRes.data?.success) {
        setSucursales(sucursalesRes.data.data || []);
      }
      
      if (permisosRes.data?.success) {
        setPermisos(permisosRes.data.data || {});
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar datos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = async (usuario = null) => {
    if (usuario) {
      setEditando(usuario);
      
      // Cargar permisos del usuario
      try {
        const permisosRes = await usuariosAPI.getPermisos(usuario.id);
        const permisosUsuario = permisosRes.data?.data || [];
        
        setFormData({
          id: usuario.id,
          username: usuario.username,
          password: '', // No mostrar password
          nombre_completo: usuario.nombre_completo,
          email: usuario.email || '',
          telefono: usuario.telefono || '',
          rol_id: usuario.rol_id || '',
          sucursal_id: usuario.sucursal_id || '',
          comision_porcentaje: usuario.comision_porcentaje || 0,
          activo: usuario.activo,
          permisos: permisosUsuario.map(p => p.id)
        });
      } catch (error) {
        console.error('Error al cargar permisos del usuario:', error);
        setFormData({
          id: usuario.id,
          username: usuario.username,
          password: '',
          nombre_completo: usuario.nombre_completo,
          email: usuario.email || '',
          telefono: usuario.telefono || '',
          rol_id: usuario.rol_id || '',
          sucursal_id: usuario.sucursal_id || '',
          comision_porcentaje: usuario.comision_porcentaje || 0,
          activo: usuario.activo,
          permisos: []
        });
      }
    } else {
      setEditando(null);
      setFormData({
        username: '',
        password: '',
        nombre_completo: '',
        email: '',
        telefono: '',
        rol_id: '',
        sucursal_id: '',
        comision_porcentaje: 0,
        activo: 1,
        permisos: []
      });
    }
    setShowModal(true);
    setShowPassword(false);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setFormData({
      username: '',
      password: '',
      nombre_completo: '',
      email: '',
      telefono: '',
      rol_id: '',
      sucursal_id: '',
      comision_porcentaje: 0,
      activo: 1,
      permisos: []
    });
    setShowPermisos(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre_completo.trim()) {
      alert('El nombre completo es requerido');
      return;
    }
    
    if (!editando && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }
    
    if (!formData.rol_id) {
      alert('Debe seleccionar un rol');
      return;
    }

    try {
      setLoading(true);
      
      // Preparar datos (no enviar password vacío en edición)
      const dataToSend = { ...formData };
      if (editando && !dataToSend.password) {
        delete dataToSend.password;
      }
      
      let response;
      if (editando) {
        response = await usuariosAPI.update(dataToSend);
        alert('Usuario actualizado exitosamente');
      } else {
        response = await usuariosAPI.create(dataToSend);
        alert('Usuario creado exitosamente');
      }
      
      // Actualizar permisos si hay alguno seleccionado
      if (dataToSend.permisos && dataToSend.permisos.length > 0) {
        const userId = editando ? editando.id : response.data?.data?.id;
        if (userId) {
          await usuariosAPI.updatePermisos(userId, dataToSend.permisos);
        }
      }
      
      cerrarModal();
      cargarDatos();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const eliminarUsuario = async (id) => {
    if (id === user?.id) {
      alert('No puedes eliminar tu propio usuario');
      return;
    }
    
    if (!window.confirm('¿Estás seguro de desactivar este usuario?')) {
      return;
    }

    try {
      await usuariosAPI.delete(id);
      alert('Usuario desactivado exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const getRolBadgeColor = (nivel) => {
    if (nivel >= 100) return 'bg-purple-100 text-purple-800 border-purple-300';
    if (nivel >= 80) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (nivel >= 60) return 'bg-indigo-100 text-indigo-800 border-indigo-300';
    if (nivel >= 40) return 'bg-green-100 text-green-800 border-green-300';
    if (nivel >= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getRolNivel = (usuario) => {
    // Si tiene rol_nivel de la tabla roles, usarlo
    if (usuario.rol_nivel) return usuario.rol_nivel;
    
    // Si no, determinar nivel por el campo rol ENUM
    switch (usuario.rol) {
      case 'admin': return 100;
      case 'gerente': return 80;
      case 'inventario': return 50;
      case 'cajero': return 40;
      case 'vendedor': return 30;
      default: return 10;
    }
  };

  if (loading && usuarios.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-primary-600" />
              Gestión de Usuarios
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} registrado{usuarios.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => abrirModal()}
            className="btn-primary flex items-center gap-2 justify-center"
          >
            <Plus className="w-5 h-5" />
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Tabla de Usuarios */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Contacto
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-bold text-lg">
                            {usuario.nombre_completo.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {usuario.nombre_completo}
                        </div>
                        <div className="text-sm text-gray-500">
                          @{usuario.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRolBadgeColor(getRolNivel(usuario))}`}>
                      <Shield className="w-3 h-3 mr-1" />
                      {usuario.rol_nombre || (usuario.rol ? usuario.rol.charAt(0).toUpperCase() + usuario.rol.slice(1) : 'Sin rol')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                    <div className="text-sm text-gray-900">
                      {usuario.email && (
                        <div className="flex items-center gap-1 mb-1">
                          <Mail className="w-3 h-3 text-gray-400" />
                          {usuario.email}
                        </div>
                      )}
                      {usuario.telefono && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400" />
                          {usuario.telefono}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {usuario.activo ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        <UserCheck className="w-3 h-3 mr-1" />
                        Activo
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        <UserX className="w-3 h-3 mr-1" />
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => abrirModal(usuario)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {usuario.id !== user?.id && (
                        <button
                          onClick={() => eliminarUsuario(usuario.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Desactivar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {usuarios.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
            <p className="mt-1 text-sm text-gray-500">Comienza creando un nuevo usuario</p>
          </div>
        )}
      </div>

      {/* Modal Crear/Editar Usuario */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6 text-primary-600" />
                  {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>
                <button
                  onClick={cerrarModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nombre Completo */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})}
                    className="input-field"
                    placeholder="Juan Pérez García"
                    required
                  />
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Usuario <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="input-field"
                    placeholder="juanperez"
                    required
                    disabled={editando}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña {!editando && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="input-field pr-10"
                      placeholder={editando ? "Dejar en blanco para mantener" : "••••••••"}
                      required={!editando}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-field pl-10"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                </div>

                {/* Teléfono */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                      className="input-field pl-10"
                      placeholder="1234-5678"
                    />
                  </div>
                </div>

                {/* Rol */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={formData.rol_id}
                      onChange={(e) => setFormData({...formData, rol_id: e.target.value})}
                      className="input-field pl-10"
                      required
                    >
                      <option value="">Seleccionar rol...</option>
                      {roles.map(rol => (
                        <option key={rol.id} value={rol.id}>
                          {rol.nombre} - Nivel {rol.nivel}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sucursal */}
                {sucursales.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sucursal
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={formData.sucursal_id}
                        onChange={(e) => setFormData({...formData, sucursal_id: e.target.value})}
                        className="input-field pl-10"
                      >
                        <option value="">Todas las sucursales</option>
                        {sucursales.map(suc => (
                          <option key={suc.id} value={suc.id}>
                            {suc.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Comisión */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comisión (%)
                  </label>
                  <input
                    type="number"
                    value={formData.comision_porcentaje}
                    onChange={(e) => setFormData({...formData, comision_porcentaje: e.target.value})}
                    className="input-field"
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>

                {/* Estado */}
                {editando && (
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => setFormData({...formData, activo: e.target.checked ? 1 : 0})}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Usuario activo</span>
                    </label>
                  </div>
                )}

                {/* Permisos Individuales */}
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setShowPermisos(!showPermisos)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Lock className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Permisos Individuales</span>
                      {formData.permisos.length > 0 && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-800 rounded-full">
                          {formData.permisos.length}
                        </span>
                      )}
                    </span>
                    <span className="text-gray-400">
                      {showPermisos ? '▲' : '▼'}
                    </span>
                  </button>

                  {showPermisos && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                      {Object.keys(permisos).length > 0 ? (
                        <div className="space-y-4">
                          {Object.entries(permisos).map(([modulo, permisosList]) => (
                            <div key={modulo} className="border border-gray-200 rounded-lg p-3 bg-white">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 capitalize">{modulo}</h4>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const moduloIds = permisosList.map(p => p.id);
                                    const allSelected = moduloIds.every(id => formData.permisos.includes(id));
                                    if (allSelected) {
                                      setFormData({
                                        ...formData,
                                        permisos: formData.permisos.filter(id => !moduloIds.includes(id))
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        permisos: [...new Set([...formData.permisos, ...moduloIds])]
                                      });
                                    }
                                  }}
                                  className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded hover:bg-primary-100"
                                >
                                  Seleccionar todos
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {permisosList.map(permiso => (
                                  <label
                                    key={permiso.id}
                                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.permisos.includes(permiso.id)}
                                      onChange={() => {
                                        if (formData.permisos.includes(permiso.id)) {
                                          setFormData({
                                            ...formData,
                                            permisos: formData.permisos.filter(id => id !== permiso.id)
                                          });
                                        } else {
                                          setFormData({
                                            ...formData,
                                            permisos: [...formData.permisos, permiso.id]
                                          });
                                        }
                                      }}
                                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700">{permiso.nombre}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">No hay permisos disponibles</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="flex-1 btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : (editando ? 'Actualizar' : 'Crear Usuario')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosPage;
